import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Box, Typography, Button } from "@mui/material";
import { subWeeks, parseJSON, isBefore } from "date-fns";
import AddIcon from "@icons/myicons/plus-circled.svg?react";
import { Investment } from "@rentablo/api/apiv1";
import {
  getValueByInvestmentAndBookingType,
  toFirstTwoSignificantDecimal,
  toCurrency,
} from "@helpers";
import { InvestmentInfo } from "@components";
import { TableConfig, GainLossPercents } from "@rentablo/components";
import { useCurrencyDisplay } from "@providers/CurrencyDisplayProvider";
import {
  getDisplayedInvestment,
  useDisplayedInvestment,
} from "./useDisplayedInvestment";
import { getDisplayedQuoteValue } from "./getDisplayedQuoteValue";

const QUOTE_PROVIDER_NONE = "none";

// No price yet, or the last one is more than two weeks old.
export const isQuoteOutdated = ({ quote }: Investment) =>
  !quote?.lastDateTime ||
  isBefore(parseJSON(quote.lastDateTime), subWeeks(new Date(), 2));

export const checkIsShowModal = (investment: Investment) =>
  investment.quoteProvider === QUOTE_PROVIDER_NONE ||
  isQuoteOutdated(investment);

export type InvestmentTableAdditionalProps = {
  formatCurrency: (value: number, currency?: string) => string;
  images: Record<string, string | null | undefined>;
  onRowClick?: (url: string) => void;
  onClickAddQuoteModal: (investment: Investment) => void;
};

type RendererProps = {
  row: Investment;
  tableKey: string;
  isChildRow?: boolean;
} & Partial<InvestmentTableAdditionalProps>;

const NameCell = ({ row, images = {}, onRowClick }: RendererProps) => {
  const image = images[row.isin || ""] || images[row?.tickerSymbol || ""];

  return (
    <InvestmentInfo
      item={row}
      image={image}
      link={null}
      onClick={
        onRowClick
          ? (e?: React.MouseEvent) => {
              e?.stopPropagation();
              onRowClick(row.id.toString());
            }
          : undefined
      }
    />
  );
};

const QuoteCell = ({ row, onClickAddQuoteModal }: RendererProps) => {
  const { t } = useTranslation();
  const { selectedCurrencyView, getDisplayCurrency } = useCurrencyDisplay();
  const isOriginal = selectedCurrencyView === "original";
  const currency = getDisplayCurrency(row.currency);

  return (
    <Box display="inline-flex" alignItems="center" gap={1}>
      <Typography variant="body4" noWrap>
        {getValueByInvestmentAndBookingType({
          value: getDisplayedQuoteValue(row.quote, isOriginal),
          investmentType: row.type,
          currency,
          percentageRange: "0-100",
          quoteNotation: row.quoteNotation,
        })}{" "}
      </Typography>
      {checkIsShowModal(row) && (
        <Button
          variant="tertiary"
          size="small"
          startIcon={<AddIcon />}
          onClick={(e) => {
            e.stopPropagation();
            onClickAddQuoteModal?.(row);
          }}
          sx={{ marginLeft: 2 }}
        >
          {t("domains.investments.investmentTable.addQuote.quote")}
        </Button>
      )}
      {Boolean(row.quote?.changePercentAbs) && (
        <Box ml={2}>
          <GainLossPercents value={row.quote?.changePercentAbs ?? 0} />
        </Box>
      )}
    </Box>
  );
};

const PurchaseCell = ({ row, formatCurrency = toCurrency }: RendererProps) => {
  const { t } = useTranslation();
  const { currency, purchaseValue, purchasePrice } =
    useDisplayedInvestment(row);

  return (
    <>
      <Typography variant="body4">
        {purchaseValue ? formatCurrency(purchaseValue, currency) : ""}
      </Typography>
      <Typography variant="caption2" color="textSecondary">
        {toFirstTwoSignificantDecimal(row.numberOfLots)}{" "}
        {t("domains.investments.investmentTable.pieces")}{" "}
        {getValueByInvestmentAndBookingType({
          value: purchasePrice ?? 0,
          investmentType: row.type,
          currency,
          quoteNotation: row.quoteNotation,
        })}
      </Typography>
    </>
  );
};

const ChangeCell = ({ row, formatCurrency = toCurrency }: RendererProps) => {
  const { t } = useTranslation();
  const {
    currency,
    currentMarketValue,
    returnOnInvestmentInPercent,
    receivedTotalDividend,
  } = useDisplayedInvestment(row);

  return (
    <>
      <Box
        display="flex"
        justifyContent="flex-end"
        alignItems="center"
        flexWrap="wrap"
      >
        <Typography variant="body4">
          {currentMarketValue
            ? formatCurrency(currentMarketValue, currency)
            : ""}
        </Typography>
        <Box ml={2}>
          <GainLossPercents value={returnOnInvestmentInPercent!} />
        </Box>
      </Box>
      {Boolean(receivedTotalDividend) && (
        <Typography variant="caption2" color="textSecondary" fontWeight={600}>
          {receivedTotalDividend
            ? formatCurrency(receivedTotalDividend, currency)
            : ""}
          {t("domains.investments.investmentTable.dividends")}
        </Typography>
      )}
    </>
  );
};

export const useTableConfig = (
  onRowClick?: (investment: Investment) => void
): TableConfig<Investment> => {
  const { t } = useTranslation();
  const { selectedCurrencyView } = useCurrencyDisplay();

  return useMemo(() => {
    const isOriginal = selectedCurrencyView === "original";
    const displayed = (row: Investment) =>
      getDisplayedInvestment(row, selectedCurrencyView === "global");

    return {
      id: "investments-table",
      onRowClick,
      sorting: {
        field: "name",
        direction: "ASC" as const,
      },
      loadingRows: 3,
      loadingRowsHeight: "44px",
      getRowId: (data: Investment) => data.id,
      columns: [
        {
          id: "name",
          title: t("domains.investments.investmentTable.name"),
          sortable: true,
          sortValue: (row) => row.standardisedName || row.name,
          renderer: NameCell,
        },
        {
          id: "quote",
          sortable: true,
          sortParts: [
            {
              title: t("domains.investments.investmentTable.quote"),
              sortKey: "quote.last",
              sortValue: (row) => getDisplayedQuoteValue(row.quote, isOriginal),
            },
            {
              title: t("domains.investments.investmentTable.changePercentAbs"),
              sortKey: "quote.changePercentAbs",
            },
          ],
          renderer: QuoteCell,
        },
        {
          id: "purchase",
          title: t("domains.investments.investmentTable.purchase"),
          sortable: true,
          sortKey: "purchaseValue",
          sortValue: (row) => displayed(row).purchaseValue,
          renderer: PurchaseCell,
        },
        {
          id: "change",
          sortable: true,
          sortParts: [
            {
              title: t("domains.investments.investmentTable.totalValue"),
              sortKey: "currentMarketValue",
              sortValue: (row) => displayed(row).currentMarketValue,
            },
            {
              title: t("domains.investments.investmentTable.change"),
              sortKey: "returnOnInvestmentInPercent",
              sortValue: (row) => displayed(row).returnOnInvestmentInPercent,
            },
          ],
          align: "right" as const,
          renderer: ChangeCell,
        },
      ],
    };
  }, [t, onRowClick, selectedCurrencyView]);
};
